import React from 'react'

import { Draggable, Droppable } from 'react-beautiful-dnd'
import { CSSObject, Drawer as MuiDrawer, Theme } from '@mui/material'
import { styled } from '@mui/material/styles'

import UserLinks from '../UserLinks'
import SpacerToolbar from '../SpacerToolbar'
import {
    setActiveDashboardId as handleClickSwitchDashboard,
    triggerDashboardReflow,
    useAllDashboardsQuery,
    useIsEditingLayoutsQuery,
} from '@edge/spark/io-dashboards'
import AdminActAsUser from './components/AdminActAsUser'
import DashboardLink from './components/DashboardLink'
import DashboardsSubtitle from './components/DashboardsSubtitle'
import ListContainer from './components/ListContainer'
import StyledDivider from './components/StyledDivider'
import { useDrawerOpenStore } from './drawerOpen.store'
import { useApolloClient } from 'react-apollo'

export const DRAWER_WIDTH = 260

const openedMixin = (theme: Theme): CSSObject => ({
    width: DRAWER_WIDTH,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
})

const closedMixin = (theme: Theme): CSSObject => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up('sm')]: {
        width: `calc(${theme.spacing(8)} + 1px)`,
    },
})

const StyledDrawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
    width: DRAWER_WIDTH,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
        ...openedMixin(theme),
        '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
        ...closedMixin(theme),
        '& .MuiDrawer-paper': closedMixin(theme),
    }),
}))

const DesktopDrawer = (): React.ReactElement => {
    // const { client } = useDesktopDrawerQuery({
    //     returnPartialData: true,
    // })
    const { data } = useIsEditingLayoutsQuery()
    const normalizedDashboards = useAllDashboardsQuery()
    const { open: gloablDrawerOpenState } = useDrawerOpenStore()
    const [hovered, setHovered] = React.useState(false)
    const expanded = gloablDrawerOpenState || hovered
    const isEditingLayouts = !!data?.isEditingLayouts
    const client = useApolloClient()

    const handleOnMouseEnter = () => setHovered(true)

    const handleOnMouseLeave = () => setHovered(false)

    React.useEffect(() => {
        client.writeData({
            data: {
                dashboardReflowTrigger: Math.random(),
            },
        })

        triggerDashboardReflow()
    }, [client, expanded])

    return (
        <div>
            <StyledDrawer open={expanded} variant="permanent" PaperProps={{ sx: { zIndex: 101 } }}>
                <SpacerToolbar />
                <ListContainer onMouseEnter={handleOnMouseEnter} onMouseLeave={handleOnMouseLeave}>
                    <DashboardsSubtitle expanded={expanded} />

                    <Droppable droppableId="droppable-dashboards-list">
                        {(dropProvided): JSX.Element => (
                            <div
                                style={{ cursor: 'move' }}
                                ref={dropProvided.innerRef}
                                {...dropProvided.droppableProps}
                            >
                                <div>
                                    {normalizedDashboards.map((d, i) => (
                                        <Draggable
                                            key={`Draggable${d.id}Key`}
                                            draggableId={d.id}
                                            index={i}
                                            isDragDisabled={isEditingLayouts}
                                        >
                                            {(dragProvided): JSX.Element => (
                                                <div
                                                    key={d.id}
                                                    ref={dragProvided.innerRef}
                                                    {...dragProvided.draggableProps}
                                                    {...dragProvided.dragHandleProps}
                                                >
                                                    <DashboardLink
                                                        id={d.id}
                                                        name={d.name}
                                                        isEditingLayouts={isEditingLayouts}
                                                        onClick={() => handleClickSwitchDashboard(d.id)}
                                                        expanded={expanded}
                                                    />
                                                </div>
                                            )}
                                        </Draggable>
                                    ))}
                                </div>
                                {dropProvided.placeholder}
                            </div>
                        )}
                    </Droppable>

                    <StyledDivider />

                    <AdminActAsUser expanded={expanded} />
                    <UserLinks />
                </ListContainer>
            </StyledDrawer>
        </div>
    )
}

export default DesktopDrawer
